/**
 * Document head per route.
 *
 * `headHtml()` renders the complete per-route head fragment for the
 * build-time prerender (title, description, robots, canonical, Open Graph,
 * Twitter card, JSON-LD). `applyHead()` keeps title, description and
 * canonical current during client-side navigation between pages.
 */

import { SITE_NAME, SITE_URL, absoluteUrl, ogImagePath, type PublicRoute, type RouteMatch } from './route';
import { jsonLdForRoute, serializeJsonLd, type FaqItem } from './json-ld';

const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export interface HeadOptions {
  /** FAQ items for the docs route's FAQPage schema */
  faq?: readonly FaqItem[];
  /** Absolute or root-relative URL of the landing hero image to preload */
  preloadImage?: string;
}

/** Full head fragment for a prerendered public route */
export function headHtml(route: PublicRoute, options: HeadOptions = {}): string {
  const url = absoluteUrl(route.path);
  const image = `${SITE_URL}${ogImagePath(route.path)}`;
  const ogType = route.kind === 'learn' ? 'article' : 'website';
  const lines = [
    `<title>${escapeText(route.title)}</title>`,
    `<meta name="description" content="${escapeAttr(route.description)}" />`,
    `<meta name="robots" content="index,follow,max-image-preview:large" />`,
    `<link rel="canonical" href="${url}" />`,
    `<meta property="og:type" content="${ogType}" />`,
    `<meta property="og:site_name" content="${SITE_NAME}" />`,
    `<meta property="og:locale" content="en_AU" />`,
    `<meta property="og:url" content="${url}" />`,
    `<meta property="og:title" content="${escapeAttr(route.title)}" />`,
    `<meta property="og:description" content="${escapeAttr(route.description)}" />`,
    `<meta property="og:image" content="${image}" />`,
    `<meta property="og:image:width" content="${OG_IMAGE_WIDTH}" />`,
    `<meta property="og:image:height" content="${OG_IMAGE_HEIGHT}" />`,
    `<meta property="og:image:alt" content="${escapeAttr(`${SITE_NAME} — ${route.label}`)}" />`,
    `<meta name="twitter:card" content="summary_large_image" />`,
    `<meta name="twitter:title" content="${escapeAttr(route.title)}" />`,
    `<meta name="twitter:description" content="${escapeAttr(route.description)}" />`,
    `<meta name="twitter:image" content="${image}" />`,
  ];
  if (route.kind === 'learn' && route.learn) {
    lines.push(`<meta property="article:published_time" content="${route.learn.published}" />`);
    lines.push(`<meta property="article:modified_time" content="${route.updated}" />`);
  }
  if (options.preloadImage) {
    lines.push(`<link rel="preload" as="image" href="${escapeAttr(options.preloadImage)}" fetchpriority="high" />`);
  }
  for (const data of jsonLdForRoute(route, options.faq)) {
    lines.push(`<script type="application/ld+json">${serializeJsonLd(data)}</script>`);
  }
  return lines.join('\n    ');
}

/** Head for the bare application shell (`dist/app.html`) and the 404 page: never indexed */
export function shellHeadHtml(title: string): string {
  return [
    `<title>${escapeText(title)}</title>`,
    `<meta name="robots" content="noindex,nofollow" />`,
    `<meta name="description" content="${escapeAttr('SigTDD time-distance diagram tool')}" />`,
  ].join('\n    ');
}

function setMeta(selector: string, attr: string, value: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    const [, kind, key] = selector.match(/^meta\[(name|property)="([^"]+)"\]$/) ?? [];
    if (kind && key) el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel: string, href: string | null): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

const APP_TITLE: Record<string, string> = {
  app: 'SigTDD — time-distance diagram workspace',
  login: 'Log in — SigTDD',
  signup: 'Create a free account — SigTDD',
  'verify-email': 'Verify your email — SigTDD',
  'forgot-password': 'Reset your password — SigTDD',
  'reset-password': 'Choose a new password — SigTDD',
};

/** Client-side head maintenance after a route change */
export function applyHead(match: RouteMatch): void {
  if (typeof document === 'undefined') return;
  if (match.kind === 'public') {
    const { route } = match;
    document.title = route.title;
    setMeta('meta[name="description"]', 'content', route.description);
    setMeta('meta[name="robots"]', 'content', 'index,follow,max-image-preview:large');
    setMeta('meta[property="og:url"]', 'content', absoluteUrl(route.path));
    setMeta('meta[property="og:title"]', 'content', route.title);
    setMeta('meta[property="og:description"]', 'content', route.description);
    setLink('canonical', absoluteUrl(route.path));
    return;
  }
  document.title =
    match.kind === 'app' ? APP_TITLE[match.page] : match.kind === 'share' ? 'Shared plan — SigTDD' : 'Page not found — SigTDD';
  setMeta('meta[name="robots"]', 'content', 'noindex,nofollow');
  setLink('canonical', null);
}
